import { useEffect, useMemo, useState } from 'react'
import type { CSSProperties, FormEvent, ReactNode } from 'react'

const API_URL = ((import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env?.VITE_HAIRLESS_API_URL
  ?? 'https://hairless-saas-api.yk445kauod.workers.dev').replace(/\/$/, '')
const TOKEN_KEY = 'hairless_access_token'

type AuthUser = {
  id?: string
  email: string
  display_name?: string | null
  account_status?: string
  is_admin?: number | boolean
}
type Plan = { id: 'free' | 'pro' | 'team'; name: string; description: string; features: string[]; priceLabel: string }
type SubscriptionRequest = { id: string; plan: 'pro' | 'team'; status: 'pending' | 'approved' | 'rejected' | 'cancelled'; payment_status: string; created_at: string }
type AuthResponse = {
  authenticated?: boolean
  accessToken?: string
  user?: AuthUser
  accountStatus?: string
  plan?: string
  status?: string
  error?: string
  message?: string
  plans?: Plan[]
  requests?: SubscriptionRequest[]
  request?: SubscriptionRequest
}

async function callAuth(path: string, init: RequestInit = {}, token?: string): Promise<AuthResponse> {
  const headers = new Headers(init.headers)
  headers.set('content-type', 'application/json')
  if (token) headers.set('authorization', `Bearer ${token}`)
  const response = await fetch(`${API_URL}${path}`, { ...init, headers, credentials: 'include' })
  const body = await response.json().catch(() => ({})) as AuthResponse
  if (!response.ok) throw new Error(body.error ?? body.message ?? `auth_request_failed_${response.status}`)
  return body
}

function friendlyError(error: unknown): string {
  const code = error instanceof Error ? error.message : 'auth_request_failed'
  const messages: Record<string, string> = {
    invalid_credentials: 'البريد الإلكتروني أو كلمة المرور غير صحيحة.',
    email_already_registered: 'البريد الإلكتروني مسجّل بالفعل.',
    valid_email_required: 'اكتب بريدًا إلكترونيًا صحيحًا.',
    password_policy: 'كلمة المرور لازم تكون 10 أحرف على الأقل وتحتوي حروف وأرقام.',
    account_locked: 'المحاولات الكثيرة قفلت الحساب مؤقتًا. جرّب بعد شوية.',
    subscription_request_exists: 'عندك طلب مفتوح للخطة دي بالفعل.',
  }
  return messages[code] ?? 'حصلت مشكلة. جرّب تاني.'
}

const fallbackPlans: Plan[] = [
  { id: 'free', name: 'Free', description: 'كل أدوات Harness الأساسية بدون تفعيل.', features: ['Workspace وملفات ومحادثة الوكيل', 'BYOK ومزوّدين النماذج', 'Web وPWA'], priceLabel: 'مجاني' },
  { id: 'pro', name: 'Pro', description: 'سعة أعلى وتوصيلات مدفوعة عند إطلاق الدفع.', features: ['كل مميزات Free', 'حدود استخدام أعلى', 'أولوية في التوصيلات'], priceLabel: 'قريبًا' },
  { id: 'team', name: 'Team', description: 'مساحات فريق وإدارة مركزية.', features: ['كل مميزات Pro', 'إدارة أعضاء الفريق', 'تحكم Admin متقدم'], priceLabel: 'قريبًا' },
]

export function HairlessAuthGate(props: { children: ReactNode }) {
  const [token, setToken] = useState<string | undefined>(() => sessionStorage.getItem(TOKEN_KEY) ?? undefined)
  const [user, setUser] = useState<AuthUser | undefined>()
  const [checking, setChecking] = useState(true)
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [currentPlan, setCurrentPlan] = useState('free')
  const [planStatus, setPlanStatus] = useState('active')
  const [plans, setPlans] = useState<Plan[]>([])
  const [requests, setRequests] = useState<SubscriptionRequest[]>([])
  const [planBusy, setPlanBusy] = useState<string | null>(null)

  async function loadPlanData(currentToken: string) {
    const [planResponse, requestResponse] = await Promise.all([
      fetch(`${API_URL}/api/plans`, { headers: { authorization: `Bearer ${currentToken}` } }),
      callAuth('/api/subscription-requests', {}, currentToken),
    ])
    const planPayload = await planResponse.json() as { plans?: Plan[] }
    setPlans(planPayload.plans || [])
    setRequests(requestResponse.requests || [])
  }

  useEffect(() => {
    let cancelled = false
    if (!token) { setChecking(false); return () => { cancelled = true } }
    void callAuth('/api/auth/me', {}, token)
      .then((result) => {
        if (!cancelled && result.user) {
          setUser(result.user)
          setCurrentPlan(result.plan || 'free')
          setPlanStatus(result.status || result.accountStatus || 'active')
          void loadPlanData(token)
        }
      })
      .catch(() => {
        sessionStorage.removeItem(TOKEN_KEY)
        if (!cancelled) { setToken(undefined); setUser(undefined) }
      })
      .finally(() => { if (!cancelled) setChecking(false) })
    return () => { cancelled = true }
  }, [token])

  const title = useMemo(() => mode === 'login' ? 'ارجع لمساحتك البرمجية' : 'ابدأ مساحة Hairless بتاعتك', [mode])

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy(true); setError(''); setNotice('')
    try {
      const result = await callAuth(`/api/auth/${mode}`, { method: 'POST', body: JSON.stringify({ email, password, ...(mode === 'register' ? { displayName } : {}) }) })
      if (!result.accessToken || !result.user) throw new Error('auth_response_incomplete')
      sessionStorage.setItem(TOKEN_KEY, result.accessToken)
      setToken(result.accessToken)
      setUser(result.user)
      setCurrentPlan(result.plan || 'free')
      setPlanStatus(result.status || result.accountStatus || 'active')
      setPassword('')
      void loadPlanData(result.accessToken)
      if (mode === 'register') setNotice('حسابك المجاني اتفتح فورًا. كل أدوات Harness الأساسية متاحة من دلوقتي.')
    } catch (requestError) { setError(friendlyError(requestError)) } finally { setBusy(false) }
  }

  async function requestPlan(plan: 'pro' | 'team') {
    if (!token) return
    setPlanBusy(plan); setError(''); setNotice('')
    try {
      const result = await callAuth('/api/subscription-requests', { method: 'POST', body: JSON.stringify({ plan }) }, token)
      if (result.request) setRequests((current) => [result.request as SubscriptionRequest, ...current])
      setNotice(`طلب ${plan.toUpperCase()} اتسجل. الدفع عبر Paymob هيتوصل لاحقًا، وحسابك المجاني يفضل شغال بالكامل.`)
    } catch (requestError) { setError(friendlyError(requestError)) } finally { setPlanBusy(null) }
  }

  async function logout() {
    const currentToken = token
    sessionStorage.removeItem(TOKEN_KEY)
    setToken(undefined); setUser(undefined); setCurrentPlan('free'); setRequests([])
    if (currentToken) await callAuth('/api/auth/logout', { method: 'POST' }, currentToken).catch(() => undefined)
  }

  if (checking) return <div style={styles.loading} dir="rtl"><img src="/hairless-falcon.svg" alt="Hairless" style={styles.loadingLogo} /><span>Hairless بيجهّز مساحة الكود…</span></div>

  if (!user) return (
    <main style={styles.page} dir="rtl">
      <section style={styles.authLayout}>
        <div style={styles.brandPanel}>
          <div style={styles.brandLockup}><img src="/hairless-falcon.svg" alt="Hairless Falcon" style={styles.logo} /><div><strong style={styles.wordmark}>HAIRLESS</strong><span style={styles.slogan}>the agentic coding harness</span></div></div>
          <div style={styles.orangeRule} />
          <p style={styles.kicker}>AGENTIC CODING / OPEN WORKSPACE</p>
          <h1 style={styles.heroTitle}>المشروع بتاعك.<br /><em>الوكيل بتاعك.</em></h1>
          <p style={styles.heroCopy}>اكتب، افحص، شغّل، وعدّل الكود من Harness واحد. Hairless يحافظ على تجربة المشروع الأصلية ويديها هوية وأدوات وتوصيلات بتاعتنا.</p>
          <div style={styles.featureGrid}><span>▣ مشاريع وملفات</span><span>◈ محادثات طويلة</span><span>⌘ أدوات الوكيل</span><span>◉ BYOK ومزوّدين</span></div>
        </div>
        <section style={styles.card} aria-label="Hairless authentication">
          <div style={styles.cardTop}><span style={styles.cardEyebrow}>{mode === 'login' ? 'WELCOME BACK' : 'CREATE FREE ACCOUNT'}</span><span style={styles.liveChip}><i /> Worker online</span></div>
          <h2 style={styles.heading}>{title}</h2>
          <p style={styles.subtitle}>{mode === 'login' ? 'سجّل دخولك وكمل من آخر Session.' : 'الحساب المجاني مش محتاج تفعيل أدمن وبيفتح النسخة الأساسية كاملة.'}</p>
          <form onSubmit={submit} style={styles.form}>
            {mode === 'register' && <label style={styles.label}>الاسم<input style={styles.input} value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder="اسمك" autoComplete="name" required /></label>}
            <label style={styles.label}>البريد الإلكتروني<input style={styles.input} type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" autoComplete="email" required /></label>
            <label style={styles.label}>كلمة المرور<input style={styles.input} type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="10 أحرف وحروف وأرقام" autoComplete={mode === 'login' ? 'current-password' : 'new-password'} required /></label>
            {error && <div style={styles.error} role="alert">{error}</div>}{notice && <div style={styles.notice} role="status">{notice}</div>}
            <button style={styles.primaryButton} type="submit" disabled={busy}>{busy ? 'جارِ فتح المساحة…' : mode === 'login' ? 'دخول إلى Hairless ↗' : 'إنشاء حساب مجاني ↗'}</button>
          </form>
          <button style={styles.linkButton} type="button" onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); setNotice('') }}>{mode === 'login' ? 'لسه جديد؟ اعمل حساب مجاني' : 'عندك حساب؟ سجّل دخول'}</button>
          <p style={styles.privacy}>مصادقة Hairless الداخلية فقط · مفيش Google أو GitHub · الدفع والخطط المدفوعة هتتضاف لاحقًا</p>
        </section>
      </section>
    </main>
  )

  const visiblePlans = plans.length ? plans : fallbackPlans
  return <div style={styles.authenticated}>
    <div style={styles.sessionBar}><span><b style={styles.sessionDot} />{user.display_name || user.email} · {currentPlan}</span><button style={styles.logout} type="button" onClick={() => void logout()}>خروج</button></div>
    <section style={styles.planStrip} dir="rtl"><div style={styles.planIntro}><span style={styles.planKicker}>HAIRLESS PLANS / {planStatus.toUpperCase()}</span><h2 style={styles.planHeading}>اختار مساحتك، وسيب الـ Harness يشتغل.</h2><p style={styles.planText}>النسخة المجانية شغالة بكل أدوات Harness الأساسية. الخطط المدفوعة بتسجل طلب فقط لحد ما نوصل بوابة Paymob.</p>{notice && <div style={styles.notice} role="status">{notice}</div>}{error && <div style={styles.error} role="alert">{error}</div>}</div><div style={styles.planCards}>{visiblePlans.map((plan) => { const isCurrent = plan.id === currentPlan; const existing = requests.find((item) => item.plan === plan.id && ['pending', 'approved'].includes(item.status)); return <article key={plan.id} style={{ ...styles.planCard, ...(isCurrent ? styles.planCardCurrent : {}) }}><div style={styles.planCardTop}><span style={styles.planName}>{plan.name}</span><span style={styles.planPrice}>{plan.priceLabel}</span></div><p style={styles.planDescription}>{plan.description}</p><ul style={styles.planFeatures}>{plan.features.map((feature) => <li key={feature}>{feature}</li>)}</ul>{plan.id === 'free' ? <span style={styles.planCurrent}>متاح الآن</span> : <button style={styles.planButton} type="button" disabled={Boolean(planBusy) || Boolean(existing)} onClick={() => void requestPlan(plan.id)}>{planBusy === plan.id ? 'جارِ تسجيل الطلب…' : existing ? (existing.status === 'approved' ? 'مفعّل' : 'الطلب مسجل') : `اطلب ${plan.name}`}</button>}</article> })}</div></section>
    {props.children}
  </div>
}

const styles: Record<string, CSSProperties> = {
  loading: { minHeight: '100%', display: 'grid', placeItems: 'center', gap: 14, color: '#f4f4f5', background: '#121212', fontFamily: 'IBM Plex Sans Arabic, system-ui, sans-serif' },
  loadingLogo: { width: 56, height: 56, objectFit: 'contain' },
  page: { minHeight: '100%', boxSizing: 'border-box', padding: 'clamp(20px, 5vw, 72px)', background: 'radial-gradient(circle at 18% 4%, #3a2116 0, #181818 34%, #0e0e0e 78%)', color: '#fff', fontFamily: 'IBM Plex Sans Arabic, system-ui, sans-serif' },
  authLayout: { width: 'min(1180px, 100%)', minHeight: 'min(720px, calc(100vh - 80px))', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr minmax(340px, 460px)', gap: 'clamp(30px, 7vw, 120px)', alignItems: 'center' },
  brandPanel: { padding: '12px 0' },
  brandLockup: { display: 'flex', alignItems: 'center', gap: 16 },
  logo: { width: 72, height: 72, objectFit: 'contain', borderRadius: 18, boxShadow: '0 14px 40px rgba(255,107,44,.18)' },
  wordmark: { display: 'block', fontSize: 28, letterSpacing: 5, lineHeight: 1 },
  slogan: { display: 'block', marginTop: 8, color: '#ff9a70', fontSize: 13, letterSpacing: .5 },
  orangeRule: { width: 84, height: 5, margin: '56px 0 24px', borderRadius: 999, background: '#ff6b2c' },
  kicker: { color: '#ff9a70', fontSize: 12, letterSpacing: 3, fontWeight: 800 },
  heroTitle: { margin: '14px 0 20px', fontSize: 'clamp(40px, 6vw, 78px)', lineHeight: 1.04, letterSpacing: -2, fontWeight: 850 },
  heroCopy: { maxWidth: 590, color: '#bdbdbd', fontSize: 18, lineHeight: 1.8 },
  featureGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 190px))', gap: 12, marginTop: 42, color: '#eee', fontSize: 14 },
  card: { boxSizing: 'border-box', padding: 'clamp(24px, 4vw, 40px)', border: '1px solid rgba(255,154,112,.22)', borderRadius: 28, background: 'linear-gradient(145deg, rgba(31,31,31,.95), rgba(17,17,17,.98))', boxShadow: '0 28px 100px rgba(0,0,0,.42)' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  cardEyebrow: { color: '#ff9a70', fontSize: 11, letterSpacing: 2, fontWeight: 800 },
  liveChip: { color: '#99e5ad', fontSize: 11 },
  heading: { margin: '26px 0 8px', fontSize: 30, lineHeight: 1.25 },
  subtitle: { margin: '0 0 26px', color: '#999', fontSize: 14, lineHeight: 1.7 },
  form: { display: 'grid', gap: 16 },
  label: { display: 'grid', gap: 8, color: '#ddd', fontSize: 13 },
  input: { width: '100%', boxSizing: 'border-box', padding: '14px 15px', border: '1px solid #46413e', borderRadius: 12, outline: 'none', background: '#0c0c0c', color: '#fff', fontSize: 15 },
  primaryButton: { border: 0, borderRadius: 12, padding: '15px 18px', background: '#ff6b2c', color: '#171717', cursor: 'pointer', fontWeight: 850, fontSize: 15 },
  linkButton: { width: '100%', marginTop: 18, border: 0, background: 'transparent', color: '#ff9a70', cursor: 'pointer', fontSize: 14 },
  error: { marginTop: 10, padding: 11, borderRadius: 10, background: '#3a1c1c', color: '#ffb4a3', fontSize: 13 },
  notice: { marginTop: 10, padding: 11, borderRadius: 10, background: '#1d3528', color: '#a7e8bd', fontSize: 13 },
  privacy: { margin: '24px 0 0', color: '#777', fontSize: 11, lineHeight: 1.7, textAlign: 'center' },
  authenticated: { minHeight: '100%', display: 'flex', flexDirection: 'column' },
  planStrip: { display: 'grid', gridTemplateColumns: 'minmax(260px, .75fr) minmax(0, 1.5fr)', gap: 22, margin: '74px auto 18px', padding: '22px 24px', width: 'min(1240px, calc(100% - 32px))', boxSizing: 'border-box', border: '1px solid rgba(255,154,112,.22)', borderRadius: 22, background: 'linear-gradient(135deg, rgba(42,27,21,.94), rgba(18,18,18,.96))' },
  planIntro: { alignSelf: 'center' },
  planKicker: { color: '#ff9a70', fontSize: 10, letterSpacing: 2, fontWeight: 800 },
  planHeading: { margin: '10px 0 8px', color: '#fff', fontSize: 23 },
  planText: { margin: 0, color: '#bdbdbd', fontSize: 13, lineHeight: 1.7 },
  planCards: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 },
  planCard: { display: 'flex', flexDirection: 'column', padding: 16, border: '1px solid rgba(255,255,255,.1)', borderRadius: 16, background: 'rgba(9,9,9,.56)' },
  planCardCurrent: { borderColor: 'rgba(255,107,44,.7)', boxShadow: 'inset 0 2px 0 #ff6b2c' },
  planCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  planName: { color: '#fff', fontWeight: 800, fontSize: 18 },
  planPrice: { color: '#ff9a70', fontSize: 12 },
  planDescription: { minHeight: 40, color: '#aaa', fontSize: 12, lineHeight: 1.6 },
  planFeatures: { margin: '0 0 14px', paddingInlineStart: 18, color: '#d8d8d8', fontSize: 11, lineHeight: 1.8 },
  planButton: { marginTop: 'auto', border: 0, borderRadius: 9, padding: '10px 12px', background: '#ff6b2c', color: '#171717', cursor: 'pointer', fontWeight: 800 },
  planCurrent: { marginTop: 'auto', padding: '10px 12px', borderRadius: 9, background: 'rgba(112,223,141,.12)', color: '#99e5ad', textAlign: 'center', fontSize: 12, fontWeight: 800 },
  sessionBar: { position: 'fixed', zIndex: 50, top: 10, insetInlineEnd: 12, display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px 7px 13px', border: '1px solid rgba(255,107,44,.35)', borderRadius: 999, background: 'rgba(20,20,20,.92)', color: '#e8e8e8', fontSize: 12 },
  sessionDot: { display: 'inline-block', width: 7, height: 7, marginInlineEnd: 7, borderRadius: '50%', background: '#70df8d' },
  logout: { border: 0, borderRadius: 999, padding: '6px 11px', background: '#ff6b2c', color: '#171717', cursor: 'pointer', fontSize: 11, fontWeight: 800 },
}
